import React from "react";
import { isEmbedded } from "src/utils/appBridgeInit";
import { DEFAULT_DASHBOARD_LAYOUT } from "../layout/constants";
import type { DashboardWidgetId } from "../layout/types";

type SkeletonWidgetLayout = { id: DashboardWidgetId; visible?: boolean };
type SkeletonLayout = { left: SkeletonWidgetLayout[]; right: SkeletonWidgetLayout[] };

/** Keep boot / route skeletons in sync with the live dashboard layout. */
const DEFAULT_SKELETON_LAYOUT: SkeletonLayout = {
  left: DEFAULT_DASHBOARD_LAYOUT.left
    .filter((w) => w.visible !== false)
    .map((w) => ({ id: w.id, visible: w.visible })),
  right: DEFAULT_DASHBOARD_LAYOUT.right
    .filter((w) => w.visible !== false)
    .map((w) => ({ id: w.id, visible: w.visible })),
};

interface DashboardSkeletonProps {
  layout?: SkeletonLayout;
}

/** Polaris Skeleton* tones — gray only, no brand colors (boot / LCP-safe). */
const TONE = "#e1e3e5";
const TONE_SOFT = "#eceef0";

const bar = (
  width: number | string,
  height: number,
  radius = 4,
): React.CSSProperties => ({
  width,
  height,
  borderRadius: radius,
  background: TONE,
  flexShrink: 0,
});

const circle = (size: number): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: "50%",
  background: TONE,
  flexShrink: 0,
});

const card: React.CSSProperties = {
  border: "1px solid #e1e3e5",
  borderRadius: 8,
  padding: 16,
  background: "#fff",
  boxSizing: "border-box",
};

const stack = (gap: number): React.CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  gap,
});

const row = (gap = 12): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap,
});

/** Welcome info banner strip. */
const WelcomeBannerSkeleton = () => (
  <div style={{ ...card, padding: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={circle(20)} />
      <div style={{ flex: 1, minWidth: 0, ...stack(8) }}>
        <div style={bar("45%", 12)} />
        <div style={bar("70%", 12)} />
      </div>
      <div style={bar(16, 16, 4)} />
    </div>
  </div>
);

/** Rating / feedback bar — taller to match live RatingBar. */
const RatingBarSkeleton = () => (
  <div style={{ ...card, padding: "18px 16px", minHeight: 72 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
        <div style={circle(44)} />
        <div style={{ flex: 1, minWidth: 0, ...stack(10) }}>
          <div style={bar(160, 14)} />
          <div style={bar("65%", 12)} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} style={circle(18)} />
          ))}
        </div>
        <div style={bar(16, 16, 4)} />
      </div>
    </div>
  </div>
);

const SectionHeader = ({ titleW, actionW }: { titleW: number; actionW?: number }) => (
  <div
    style={{
      ...card,
      padding: 10,
      background: TONE_SOFT,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={circle(18)} />
      <div style={bar(titleW, 14)} />
    </div>
    {actionW ? <div style={bar(actionW, 12)} /> : null}
  </div>
);

/** Left: Etsy Shop Stats — avatar row + detail lines. */
const ShopStatsSkeleton = () => (
  <div style={stack(12)}>
    <SectionHeader titleW={120} actionW={56} />
    <div style={card}>
      <div style={{ display: "flex", gap: 14, marginBottom: 16 }}>
        <div style={circle(56)} />
        <div style={{ flex: 1, minWidth: 0, ...stack(8) }}>
          <div style={bar(140, 16)} />
          <div style={bar(48, 12)} />
          <div style={bar("80%", 12)} />
        </div>
      </div>
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} style={{ ...row(8), padding: "8px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={bar(14, 14, 3)} />
            <div style={bar(90 + (i % 3) * 20, 12)} />
          </div>
          <div style={bar(40 + (i % 2) * 12, 18, 999)} />
        </div>
      ))}
    </div>
  </div>
);

/** Right: Listing stats — platform total + status rows. */
const ListingCardSkeleton = () => (
  <div style={card}>
    <div style={row(12)}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={circle(36)} />
        <div style={bar(100, 14)} />
      </div>
      <div style={bar(40, 24)} />
    </div>
    <div style={{ height: 1, background: TONE_SOFT, margin: "12px 0" }} />
    {Array.from({ length: 3 }, (_, i) => (
      <div key={i} style={{ ...row(8), padding: "6px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={circle(12)} />
          <div style={bar(72 + i * 12, 12)} />
        </div>
        <div style={bar(24, 12)} />
      </div>
    ))}
  </div>
);

const ListingStatsSkeleton = () => (
  <div style={stack(12)}>
    <SectionHeader titleW={96} actionW={110} />
    <ListingCardSkeleton />
  </div>
);

const OrderStatsSkeleton = () => (
  <div style={stack(12)}>
    <SectionHeader titleW={88} actionW={96} />
    <ListingCardSkeleton />
    <ListingCardSkeleton />
  </div>
);

const SubscriptionSkeleton = () => (
  <div style={card}>
    <div style={{ ...row(), marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={circle(18)} />
        <div style={bar(88, 14)} />
      </div>
      <div style={bar(72, 12)} />
    </div>
    <div style={{ ...card, background: TONE_SOFT, border: "none", ...stack(10) }}>
      <div style={row()}>
        <div style={bar(64, 18)} />
        <div style={bar(56, 16)} />
      </div>
      <div style={bar("100%", 1, 0)} />
      <div style={row()}>
        <div style={bar(72, 12)} />
        <div style={bar(64, 12)} />
      </div>
    </div>
  </div>
);

const CreditUsageSkeleton = () => (
  <div style={stack(12)}>
    <SectionHeader titleW={120} />
    <div style={card}>
      <div style={{ ...row(), marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={bar(24, 24, 6)} />
          <div style={bar(100, 14)} />
        </div>
        <div style={bar(48, 18, 999)} />
      </div>
      <div style={row()}>
        <div style={stack(6)}>
          <div style={bar(56, 18)} />
          <div style={bar(88, 12)} />
        </div>
        <div style={circle(56)} />
      </div>
    </div>
  </div>
);

const WidgetSkeleton = ({ id }: { id: DashboardWidgetId }) => {
  if (id === "etsy_shop_stats") return <ShopStatsSkeleton />;
  if (id === "order_stats") return <OrderStatsSkeleton />;
  if (id === "listing_stats") return <ListingStatsSkeleton />;
  if (id === "subscription") return <SubscriptionSkeleton />;
  if (id === "credit_usage") return <CreditUsageSkeleton />;
  return null;
};

/**
 * Lightweight dashboard skeleton for boot / route fallback.
 * Polaris-free (safe before AppProvider) — gray bars only, layout-matched, LCP-friendly.
 * No page-header chrome (title lives in Admin / PageWrapper after boot).
 */
const DashboardSkeleton = ({ layout }: DashboardSkeletonProps) => {
  const embedded = isEmbedded();
  const active = layout ?? DEFAULT_SKELETON_LAYOUT;

  return (
    <div
      className="custom_layout_width dashboard_width"
      style={{ padding: embedded ? 8 : undefined, boxSizing: "border-box" }}
    >
      <div style={stack(16)}>
        <WelcomeBannerSkeleton />
        <RatingBarSkeleton />

        <div
          className="dashboard-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 16,
            width: "100%",
          }}
        >
          <div style={stack(12)}>
            {active.left.map((w) =>
              w.visible === false ? null : <WidgetSkeleton key={w.id} id={w.id} />,
            )}
          </div>
          <div style={stack(12)}>
            {active.right.map((w) =>
              w.visible === false ? null : <WidgetSkeleton key={w.id} id={w.id} />,
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .dashboard_width .dashboard-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardSkeleton;
